import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { playClick } from '@/utils/sounds';
import XPRecap from '@/components/XPRecap';
import { AvatarCanvasSmall } from '@/components/AvatarCanvas';
import {
  RetroButton,
  bandedGradient,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const led = (size, color = PALETTE.scoreboardText) => ({
  fontFamily: DESIGN_TOKENS.fonts.display,
  fontSize: size,
  color,
  textShadow: `0 0 8px ${color}88`,
});

const panelStyle = {
  background: PALETTE.cream,
  border: `4px solid ${PALETTE.ink}`,
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
  overflow: 'hidden',
};

const bandStyle = (bg, color = PALETTE.cream) => ({
  fontFamily: DESIGN_TOKENS.fonts.display,
  fontSize: '11px',
  color,
  background: bg,
  borderBottom: `3px solid ${PALETTE.ink}`,
  letterSpacing: '1px',
});

/* Per-team batting stats table (hits by type) */
function TeamStatsTable({ teamName, players }) {
  return (
    <div className="mb-4" style={panelStyle}>
      <div className="text-center py-2" style={bandStyle(PALETTE.grass)}>
        {(teamName || 'TEAM').toUpperCase()} BOX SCORE
      </div>
      <div className="p-3">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: `2px solid ${PALETTE.ink}` }}>
              <th className="text-left py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 700, fontSize: '11px', color: PALETTE.dirt }}>PLAYER</th>
              {['1B', '2B', '3B', 'HR', 'OUT', 'K'].map((h) => (
                <th key={h} className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 700, fontSize: '11px', color: PALETTE.dirt }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.user_id} style={{ borderBottom: `1px solid ${PALETTE.ink}22` }}>
                <td className="text-left py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 600, fontSize: '13px', color: PALETTE.ink }}>{p.username}</td>
                <td className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: PALETTE.ink }}>{p.batting_stats?.singles || 0}</td>
                <td className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: PALETTE.ink }}>{p.batting_stats?.doubles || 0}</td>
                <td className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: PALETTE.ink }}>{p.batting_stats?.triples || 0}</td>
                <td className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: PALETTE.grass, fontWeight: 700 }}>{p.batting_stats?.home_runs || 0}</td>
                <td className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: '#C0392B' }}>{p.batting_stats?.outs || 0}</td>
                <td className="text-center py-1" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: PALETTE.dirt }}>{p.strikeouts_pitched || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Peanut All-Star plaque with avatar in a gold trophy frame */
function AllStarPlaque({ teamName, allStar, avatarConfig }) {
  if (!allStar) return null;
  return (
    <div
      className="text-center p-4"
      style={{
        background: bandedGradient(ACCENTS.goldLight, ACCENTS.gold, ACCENTS.goldDark),
        border: `4px solid ${PALETTE.ink}`,
        borderRadius: DESIGN_TOKENS.borderRadius.lg,
        boxShadow: `inset 2px 2px 0px #FFF3C4, 5px 5px 0px rgba(0,0,0,0.4)`,
      }}
      data-testid={`all-star-plaque-${(teamName || '').replace(/\s+/g, '-').toLowerCase()}`}
    >
      <p style={{
        fontFamily: DESIGN_TOKENS.fonts.display,
        fontSize: '10px',
        color: PALETTE.ink,
        letterSpacing: '1px',
        marginBottom: '8px',
      }}>
        ★ PEANUT ALL-STAR ★
      </p>
      <div
        className="inline-block p-2 mb-2"
        style={{
          background: PALETTE.cream,
          border: `3px solid ${PALETTE.ink}`,
          borderRadius: DESIGN_TOKENS.borderRadius.md,
          boxShadow: `2px 2px 0px ${PALETTE.ink}`,
        }}
      >
        {avatarConfig && Object.keys(avatarConfig).length > 0 ? (
          <AvatarCanvasSmall config={avatarConfig} size={64} />
        ) : (
          <span style={{ fontFamily: DESIGN_TOKENS.fonts.display, fontSize: '32px' }}>★</span>
        )}
      </div>
      <p style={{
        fontFamily: DESIGN_TOKENS.fonts.display,
        fontSize: '13px',
        color: PALETTE.ink,
        marginBottom: '4px',
      }}>
        {allStar.username}
      </p>
      <p style={{
        fontFamily: DESIGN_TOKENS.fonts.ui,
        fontWeight: 700,
        fontSize: '13px',
        color: PALETTE.ink,
      }}>
        {allStar.points} Peanut Pts • {teamName}
      </p>
    </div>
  );
}

/* Rematch coordination panel driven by polled match.rematch state */
function RematchPanel({ matchId, match, getAuthHeader, refetchMatch, userId }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const rem = match?.rematch;

  const call = async (path) => {
    playClick();
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/online/match/${matchId}/${path}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (!res.ok) throw new Error();
      if (refetchMatch) refetchMatch();
      return true;
    } catch (e) {
      toast.error('Rematch action failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    await call('rematch/decline');
    navigate('/dashboard');
  };

  if (rem?.status === 'cancelled') {
    return (
      <div className="text-center p-3 mb-4" style={panelStyle} data-testid="rematch-cancelled-note">
        <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 600, fontSize: '13px', color: '#C0392B' }}>
          Rematch declined{rem.declined_by_username ? ` by ${rem.declined_by_username}` : ''}
        </p>
      </div>
    );
  }

  if (rem?.status === 'started' || rem?.status === 'creating') {
    return (
      <div className="text-center p-3 mb-4" style={panelStyle} data-testid="rematch-starting-note">
        <p className="animate-pulse" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 600, fontSize: '13px', color: PALETTE.grass }}>
          Rematch starting...
        </p>
      </div>
    );
  }

  if (rem?.status === 'pending') {
    const accepted = rem.accepted || [];
    const total = (rem.participants || []).length;
    const iAccepted = accepted.includes(userId);
    return (
      <div className="mb-4" style={panelStyle} data-testid="rematch-pending-panel">
        <div className="text-center py-2" style={bandStyle(ACCENTS.gold, PALETTE.ink)}>
          REMATCH REQUESTED
        </div>
        <div className="p-3 text-center">
          <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '13px', color: PALETTE.ink, marginBottom: '8px' }}>
            {rem.initiator_username} wants to run it back — same teams, same settings!
          </p>
          <p style={{ fontFamily: DESIGN_TOKENS.fonts.display, fontSize: '12px', color: PALETTE.grass, marginBottom: '10px' }} data-testid="rematch-accept-count">
            {accepted.length}/{total} IN
          </p>
          {iAccepted ? (
            <>
              <p className="animate-pulse" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '12px', color: PALETTE.dirt, marginBottom: '10px' }}>
                Waiting for everyone to accept...
              </p>
              <RetroButton variant="danger" size="sm" fullWidth onClick={decline} disabled={busy} data-testid="rematch-cancel-btn">
                CANCEL REMATCH
              </RetroButton>
            </>
          ) : (
            <div className="flex gap-2">
              <RetroButton variant="primary" size="sm" fullWidth onClick={() => call('rematch')} disabled={busy} data-testid="rematch-accept-btn">
                ACCEPT
              </RetroButton>
              <RetroButton variant="danger" size="sm" fullWidth onClick={decline} disabled={busy} data-testid="rematch-decline-btn">
                DECLINE
              </RetroButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <RetroButton variant="secondary" size="md" fullWidth onClick={() => call('rematch')} disabled={busy} data-testid="rematch-btn">
        REMATCH — SAME TEAMS
      </RetroButton>
    </div>
  );
}

export default function OnlineEndGame({ matchId, getAuthHeader, match, refetchMatch }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState(null);
  const [error, setError] = useState(false);
  const [showRecap, setShowRecap] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/online/match/${matchId}/results`, {
          headers: getAuthHeader(),
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!cancelled) {
          setResults(data);
          if (data.my_xp) setTimeout(() => setShowRecap(true), 1200);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  if (error) {
    return (
      <div className="text-center p-5" style={panelStyle}>
        <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '14px', color: PALETTE.ink, marginBottom: '12px' }}>
          Could not load match results
        </p>
        <RetroButton variant="primary" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
          BACK TO DASHBOARD
        </RetroButton>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="text-center p-5" style={panelStyle}>
        <p className="animate-pulse" style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '14px', color: PALETTE.ink }}>
          Tallying the final results...
        </p>
      </div>
    );
  }

  const winnerName = results.winner === 'TIE' ? null
    : results.winner === 'A' ? results.team_a_name : results.team_b_name;

  const peanutRows = (teamPlayers) => teamPlayers
    .map((p) => ({ ...p, peanut: results.peanut_breakdown?.[p.user_id] }))
    .sort((a, b) => (b.peanut?.total_points || 0) - (a.peanut?.total_points || 0));

  const teamTotal = (teamPlayers) => teamPlayers.reduce(
    (sum, p) => sum + (results.peanut_breakdown?.[p.user_id]?.total_points || 0), 0
  );

  const findAvatar = (userId) => {
    // avatar configs come with all-star info from the results payload
    if (results.peanut_all_star_a?.user_id === userId) return results.peanut_all_star_a.avatar_config;
    if (results.peanut_all_star_b?.user_id === userId) return results.peanut_all_star_b.avatar_config;
    return null;
  };

  return (
    <div data-testid="online-endgame">
      {/* 1. FINAL SCORECARD */}
      <div className="flex justify-center mb-5">
        <div
          className="text-center px-6 py-3"
          style={{
            background: PALETTE.scoreboardBg,
            border: `4px solid ${PALETTE.ink}`,
            borderRadius: DESIGN_TOKENS.borderRadius.md,
            boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
          }}
          data-testid="online-final-score"
        >
          <p style={{ fontFamily: DESIGN_TOKENS.fonts.display, fontSize: '10px', color: '#FFFFFF', letterSpacing: '2px', marginBottom: '6px' }}>
            FINAL SCORE
          </p>
          <p style={led('26px')}>
            {results.final_score.team_a} - {results.final_score.team_b}
          </p>
          <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '11px', color: `${PALETTE.cream}99`, marginTop: '4px' }}>
            {results.team_a_name} vs {results.team_b_name}
          </p>
        </div>
      </div>

      <div className="text-center mb-6">
        <h1 style={{
          fontFamily: DESIGN_TOKENS.fonts.display,
          fontSize: DESIGN_TOKENS.fontSizes.displayXl,
          color: '#E23B2E',
          textShadow: `
            -2px -2px 0px ${PALETTE.ink}, 2px -2px 0px ${PALETTE.ink},
            -2px 2px 0px ${PALETTE.ink}, 2px 2px 0px ${PALETTE.ink},
            5px 5px 0px ${PALETTE.ink}, 7px 7px 0px rgba(0,0,0,0.4)
          `,
          letterSpacing: '4px',
          marginBottom: '12px',
        }}>
          GAME OVER
        </h1>
        <p
          style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displayMd,
            color: ACCENTS.gold,
            textShadow: `2px 2px 0px ${PALETTE.ink}, 4px 4px 0px rgba(0,0,0,0.4)`,
          }}
          data-testid="online-winner-announcement"
        >
          {winnerName ? `${winnerName.toUpperCase()} WINS!` : "IT'S A TIE!"}
        </p>
        {results.forfeit && (
          <p
            style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '11px',
              color: PALETTE.cream,
              textShadow: `1px 1px 0px ${PALETTE.ink}`,
              letterSpacing: '2px',
              marginTop: '8px',
            }}
            data-testid="forfeit-note"
          >
            WON BY FORFEIT
          </p>
        )}
      </div>

      <TeamStatsTable teamName={results.team_a_name} players={results.player_stats.team_a} />
      <TeamStatsTable teamName={results.team_b_name} players={results.player_stats.team_b} />

      {/* 2. PEANUT GALLERY REVEAL */}
      <div className="mb-4" style={panelStyle} data-testid="peanut-reveal-panel">
        <div className="text-center py-2" style={bandStyle(ACCENTS.gold, PALETTE.ink)}>
          PEANUT GALLERY REVEALED!
        </div>
        <div className="p-3">
          <p className="text-center" style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: PALETTE.dirt,
            marginBottom: '10px',
          }}>
            Secret points from the whole game, out in the open
          </p>
          {[
            { name: results.team_a_name, players: results.player_stats.team_a },
            { name: results.team_b_name, players: results.player_stats.team_b },
          ].map((team) => (
            <div key={team.name} className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span style={{ fontFamily: DESIGN_TOKENS.fonts.display, fontSize: '10px', color: PALETTE.grass, letterSpacing: '1px' }}>
                  {(team.name || '').toUpperCase()}
                </span>
                <span style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 700, fontSize: '12px', color: PALETTE.ink }} data-testid={`peanut-team-total-${(team.name || '').replace(/\s+/g, '-').toLowerCase()}`}>
                  TOTAL: {teamTotal(team.players)} pts
                </span>
              </div>
              {peanutRows(team.players).map((p) => (
                <div
                  key={p.user_id}
                  className="flex justify-between items-center px-3 py-1 mb-1"
                  style={{
                    background: '#FFFFFF',
                    border: `2px solid ${PALETTE.ink}`,
                    borderRadius: DESIGN_TOKENS.borderRadius.md,
                  }}
                  data-testid={`peanut-row-${p.username}`}
                >
                  <span style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontWeight: 600, fontSize: '13px', color: PALETTE.ink }}>
                    {p.username}
                  </span>
                  <span style={{ fontFamily: DESIGN_TOKENS.fonts.display, fontSize: '11px', color: PALETTE.dirt }}>
                    {p.peanut?.total_points || 0} pts
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* All-Star plaques */}
      <div className="grid grid-cols-1 gap-3 mb-5">
        <AllStarPlaque
          teamName={results.team_a_name}
          allStar={results.peanut_all_star_a}
          avatarConfig={results.peanut_all_star_a?.avatar_config}
        />
        <AllStarPlaque
          teamName={results.team_b_name}
          allStar={results.peanut_all_star_b}
          avatarConfig={results.peanut_all_star_b?.avatar_config}
        />
      </div>

      {/* 3. XP RECAP */}
      {results.my_xp && showRecap && (
        <div className="mb-5" data-testid="online-xp-recap">
          <XPRecap xpResult={results.my_xp} autoProgress={false} />
        </div>
      )}

      {/* 4. REMATCH */}
      <RematchPanel
        matchId={matchId}
        match={match}
        getAuthHeader={getAuthHeader}
        refetchMatch={refetchMatch}
        userId={user?.id}
      />

      <RetroButton
        variant="primary"
        size="md"
        fullWidth
        onClick={() => navigate('/dashboard')}
        data-testid="back-to-dashboard-btn"
      >
        BACK TO DASHBOARD
      </RetroButton>
    </div>
  );
}
